import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    fetchScopedAccounts,
    type Organization,
    type OrgLevel,
    type ScopedAccountIssue,
    type ScopedAccountsParams,
} from '@/api/orgAdmin';
import { filterParams } from './scopedAccountsQuery';
import type { FacetGroup, FacetGroupKey, FacetOption, ScopedAccountsFilters } from './types';

const ALL_ISSUES: ScopedAccountIssue[] = ['no_level', 'stale_role_map'];

/** One sidebar count request. `value` doubles as the option's `FacetOption.key`. */
interface FacetRequest {
    group: FacetGroupKey;
    value: string;
    params: ScopedAccountsParams;
}

export interface ScopedAccountFacetsArgs {
    apiBase: string;
    token: string | null;
    enabled: boolean;
    orgId: string | null;
    orgName: string | null;
    childOrgs: Organization[];
    activeLevels: OrgLevel[];
    filters: ScopedAccountsFilters;
}

/**
 * EAA-1839: the counted filter facets of frame 34's left column.
 *
 * ONE REQUEST PER OPTION, and that is forced. portal-api exposes no aggregate or
 * facet-count endpoint for this listing — `provisioning-status` is per-org and
 * Assignment-status shaped (synced/pending/error/revoked), not per-Level or
 * per-đơn vị — so the only way to answer "how many rows carry Level X" is to ask
 * for that filter and read `.count`. Each request therefore uses `page_size=1`:
 * the single returned row is discarded and only the total is used. The counts are
 * the reason the sidebar exists in the design, so the alternative was a sidebar of
 * unlabelled toggles.
 *
 * Split out of `useScopedAccounts` for the same reason `useRoleMapView` is split
 * out of `useRoleMapTable`: this is pure derivation over the filter state, and
 * keeping it here leaves that hook as "which rows are on screen and which are
 * ticked".
 *
 * `staleTime` is 30s so that merely paging the table (which does not change any
 * filter) never re-fires the whole fan-out.
 */
export function useScopedAccountFacets(args: ScopedAccountFacetsArgs): FacetGroup[] {
    const { t } = useTranslation();
    const { apiBase, token, enabled, orgId, orgName, childOrgs, activeLevels, filters } = args;

    const facetRequests = useMemo<FacetRequest[]>(() => {
        const requests: FacetRequest[] = [];
        for (const issue of ALL_ISSUES) {
            requests.push({
                group: 'issue',
                value: issue,
                params: { ...filterParams(filters, 'issue'), issue, pageSize: 1 },
            });
        }
        for (const level of activeLevels) {
            requests.push({
                group: 'level',
                value: level.id,
                params: { ...filterParams(filters, 'level'), levelId: level.id, pageSize: 1 },
            });
        }
        // Only fanned out while children are in scope: with `include_children` off
        // the effective scope is the single org, and any child id would make the
        // server return nothing at all (its deliberate fail-closed).
        if (filters.includeChildren && orgId) {
            for (const target of [orgId, ...childOrgs.map((child) => child.id)]) {
                requests.push({
                    group: 'childOrg',
                    value: target,
                    params: {
                        ...filterParams(filters, 'childOrg'),
                        childOrganizationId: target,
                        pageSize: 1,
                    },
                });
            }
        }
        for (const isActive of [true, false]) {
            requests.push({
                group: 'status',
                value: String(isActive),
                params: { ...filterParams(filters, 'status'), isActive, pageSize: 1 },
            });
        }
        return requests;
    }, [filters, activeLevels, childOrgs, orgId]);

    const facetQueries = useQueries({
        queries: facetRequests.map((request) => ({
            queryKey: [
                'scoped-accounts-facet',
                token,
                orgId,
                request.group,
                request.value,
                request.params,
            ],
            queryFn: () =>
                fetchScopedAccounts(apiBase, token as string, orgId as string, request.params),
            enabled,
            retry: false,
            staleTime: 30_000,
        })),
    });

    const facetCounts = useMemo(() => {
        const map = new Map<string, number | null>();
        facetRequests.forEach((request, index) => {
            const query = facetQueries[index];
            // `null`, never 0, when the request has not answered — a placeholder
            // zero beside "Chưa có Level" would read as "no problems here".
            map.set(`${request.group}:${request.value}`, query?.data?.count ?? null);
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facetRequests, facetQueries.map((q) => q.dataUpdatedAt + ':' + q.status).join('|')]);

    const countOf = useCallback(
        (group: FacetGroupKey, value: string) => facetCounts.get(`${group}:${value}`) ?? null,
        [facetCounts]
    );

    return useMemo<FacetGroup[]>(() => {
        // EAA-1845: built per-render off `t` rather than a module-level map, so the
        // facet copy reacts to the active language.
        const issueLabels: Record<ScopedAccountIssue, string> = {
            no_level: t('console.hierarchy.scopedAccounts.facets.issueNoLevel'),
            stale_role_map: t('console.hierarchy.scopedAccounts.facets.issueStaleRoleMap'),
        };
        const issueOptions: FacetOption[] = ALL_ISSUES.map((issue) => ({
            key: issue,
            label: issueLabels[issue],
            count: countOf('issue', issue),
            selected: filters.issue === issue,
            tone: 'warn' as const,
        }));
        // Kept visible but permanently inert — `issue=never_logged_in` is a
        // VALIDATION_ERROR server-side, not an unimplemented no-op.
        issueOptions.push({
            key: 'never_logged_in',
            label: t('console.hierarchy.scopedAccounts.facets.neverLoggedInLabel'),
            count: null,
            selected: false,
            disabledReason: t('console.hierarchy.scopedAccounts.facets.neverLoggedInReason'),
        });

        const levelOptions: FacetOption[] = activeLevels.map((level) => ({
            key: level.id,
            label: level.name,
            count: countOf('level', level.id),
            selected: filters.levelId === level.id,
        }));

        const childOrgOptions: FacetOption[] = [];
        if (filters.includeChildren && orgId) {
            childOrgOptions.push({
                key: orgId,
                label: t('console.hierarchy.scopedAccounts.facets.directOrgLabel', {
                    org: orgName ?? t('console.hierarchy.scopedAccounts.facets.orgFallback'),
                }),
                count: countOf('childOrg', orgId),
                selected: filters.childOrgId === orgId,
            });
            for (const child of childOrgs) {
                childOrgOptions.push({
                    key: child.id,
                    label: child.name,
                    count: countOf('childOrg', child.id),
                    selected: filters.childOrgId === child.id,
                });
            }
        }

        const statusOptions: FacetOption[] = [
            {
                key: 'true',
                label: t('console.hierarchy.scopedAccounts.facets.statusActiveLabel'),
                count: countOf('status', 'true'),
                selected: filters.isActive === true,
            },
            {
                key: 'false',
                label: t('console.hierarchy.scopedAccounts.facets.statusInactiveLabel'),
                count: countOf('status', 'false'),
                selected: filters.isActive === false,
            },
        ];

        return [
            {
                key: 'issue',
                label: t('console.hierarchy.scopedAccounts.facets.groupIssue'),
                options: issueOptions,
            },
            {
                key: 'level',
                label: t('console.hierarchy.scopedAccounts.facets.groupLevel'),
                options: levelOptions,
                note: filters.includeChildren
                    ? t('console.hierarchy.scopedAccounts.facets.levelScopeNote')
                    : undefined,
            },
            {
                key: 'childOrg',
                label: t('console.hierarchy.scopedAccounts.facets.groupOrg'),
                options: childOrgOptions,
                note: filters.includeChildren
                    ? t('console.hierarchy.scopedAccounts.facets.childOrgNote')
                    : t('console.hierarchy.scopedAccounts.facets.childOrgOffNote'),
            },
            {
                key: 'status',
                label: t('console.hierarchy.scopedAccounts.facets.groupStatus'),
                options: statusOptions,
            },
        ];
    }, [filters, activeLevels, childOrgs, orgName, orgId, countOf, t]);
}
